import React, {FC, useLayoutEffect, useState} from "react";
import {T, useTranslate} from "@tolgee/react";
import {useCurrentLocalisedShow, useCurrentShowId} from "hooks";
import {
  AppBar as MUAppBar,
  Avatar as MuiAvatar,
  Box,
  Grid,
  Toolbar,
  Typography,
  useTheme,
} from "@material-ui/core";
import {useDispatch, useSelector} from "react-redux";
import {SHOWS_PATH} from "view/routes";
import {removeProductsAttachments} from "api/order";
import {removeItem} from "tools/localStorage";
import {resetCartStepper} from "store/exhibitors/cartStepper/slice";
import {updatePopupVisibility} from "store/exhibitors/glossaryPopupTerm/slice";
import {BackButtonIcon} from "view/images/navigationIcons";
import useLocalizedLocalMap from "hooks/useLocalizedLocalMap";
import {selectMetadata} from "store/metadata/selectors";
import NavItem from "view/components/SideBar/Nav";
import {useMaintenanceModeStore} from "store/general/maintenanceMode.store";
import {useImpersonationBannerStore} from "store/general/impersonationBanner.store";
import useCurrentExhibitorPairId from "hooks/useCurrentExhibitorPairId";
import useCurrentExhibitorPair from "hooks/useCurrentExhibitorPair";
import useBranding from "store/branding/branding.store";
import {BrandingType, IExhibitorPair, Roles} from "@evennt-tech/api-v2-core";
import useViewMode, {ViewMode} from "store/viewMode/viewMode.store";
import {grey} from "@mui/material/colors";
import Link from "@material-ui/core/Link";
import clsx from "clsx";
import FlexBox from "view/components/Layout/FlexBox";
import showhubLogo from "assets/images/show/ShowhubLogo.png";
import useStyles, {SHOW_BAR_HEIGHT} from "./styles";
import Logo from "./Logo";
import UserDeatils from "./UserDetails";
import MaintenanceModePanel from "../MaintenanceModePanel";
import ImpersonationBanner from "../ImpersonationBanner";

type Props = {
  fullName: string;
  role: Roles;
  backButton?: {
    label?: string;
    path?: string;
  };
  subLabel?: string;
};

const ShowBar: FC<Props> = ({fullName, role, backButton, subLabel}) => {
  const [headerHeight, setHeaderHeight] = useState(SHOW_BAR_HEIGHT);
  const {panelHeight} = useMaintenanceModeStore();
  const bannerHeight = useImpersonationBannerStore(
    (state) => state.bannerHeight
  );
  const dispatch = useDispatch();
  const {t} = useTranslate();
  const {hideTopExhibitorBar} = useSelector(selectMetadata);

  const theme = useTheme();
  const [show] = useCurrentLocalisedShow();
  const {mode, mocked} = useViewMode();
  const classes = useStyles({
    show,
    isExhibitor: mode === ViewMode.EXHIBITOR || role === "exhibitor",
  });

  const showId = useCurrentShowId();
  let exhibitorPairId = useCurrentExhibitorPairId();

  let [exhibitorPair, connection] = useCurrentExhibitorPair({
    showId,
    exhibitorPairId,
  });

  if (mode === ViewMode.EXHIBITOR && !exhibitorPair && mocked) {
    exhibitorPair = mocked.exhibitor as IExhibitorPair;
    exhibitorPairId = mocked.exhibitor?.id as string;
  }

  const localMap = useLocalizedLocalMap(show, exhibitorPair?.stand?.builder);

  useLayoutEffect(() => {
    setHeaderHeight(panelHeight + bannerHeight + SHOW_BAR_HEIGHT);
  });

  const {brandings} = useBranding();
  const branding = brandings[showId];

  if (hideTopExhibitorBar) {
    return null;
  }

  const clearCart = async () => {
    await removeProductsAttachments();

    removeItem("productsFilters");
    removeItem("products");
    dispatch(updatePopupVisibility(true));
    dispatch(resetCartStepper());
  };
  const isExhibitor = role === "exhibitor";

  if (!show || (isExhibitor && !exhibitorPair && !connection)) {
    return null;
  }
  const backButtonLabel = t("common.nav.back_to_all", {show: localMap.show});

  let headerBackground: string | undefined;
  const transformedOpacity = !branding?.bannerOpacity
    ? 0
    : 1 - branding.bannerOpacity / 100;
  const opacity = `linear-gradient(rgba(0,0,0,${transformedOpacity}), rgba(0,0,0,${transformedOpacity}))`;
  const gradientOverlay =
    "linear-gradient(270deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 77.38%), linear-gradient(90deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 74.34%)";
  headerBackground =
    branding?.bannerType === BrandingType.COLOR
      ? branding?.bannerColor?.hex || undefined
      : `${opacity}, url("${branding?.bannerImage?.url}")`;

  const showhubReturnUrl = show.ssoConnection
    ? connection?.externalUrl
    : undefined;

  return (
    <MUAppBar
      position="fixed"
      style={{
        height: headerHeight,
        backgroundImage:
          branding?.bannerType === BrandingType.IMAGE
            ? headerBackground
            : undefined,
        backgroundColor:
          branding?.bannerType === BrandingType.COLOR
            ? headerBackground
            : undefined,
      }}
      className={classes.appBar}
    >
      <ImpersonationBanner fullName={fullName} />
      <MaintenanceModePanel />
      <Toolbar className={classes.toolbar}>
        <Box className={classes.sideLeft}>
          <Box className={classes.sideText}>
            <NavItem
              disabled={mode === ViewMode.EXHIBITOR}
              color={grey[400]}
              onClick={clearCart}
              icon={BackButtonIcon}
              name={backButton?.label || backButtonLabel}
              to={backButton?.path || SHOWS_PATH}
              subLabel={subLabel}
            />
          </Box>
        </Box>
        <FlexBox gap={2} alignCenter>
          {!!showhubReturnUrl && (
            <Box className={clsx(classes.contrastWrapper, classes.showhubBtn)}>
              <Link
                style={{color: "inherit"}}
                component="a"
                href={showhubReturnUrl}
                target="_blank"
              >
                <T
                  keyName="common.nav.back_to_showhub"
                  params={{
                    logo: (
                      <img
                        src={showhubLogo}
                        alt="SH"
                        className={classes.showhubLogo}
                      />
                    ),
                  }}
                />
              </Link>
            </Box>
          )}
          <Box
            style={{
              padding: theme.spacing(0, 2),
            }}
            className={classes.contrastWrapper}
          >
            <UserDeatils
              show={show}
              fullName={fullName}
              isExhibitor={isExhibitor}
              exhibitorPair={exhibitorPair}
              exhibitorConnection={connection}
              role={role}
              isWhiteColor="white"
            />
          </Box>
        </FlexBox>
      </Toolbar>
      <Grid
        container
        direction="row"
        justify="space-between"
        alignItems="flex-start"
        className={classes.showInfoBar}
      >
        <Box
          className={`${classes.contrastWrapper} ${classes.showNameContainer}`}
        >
          <Grid item className={classes.showInfo}>
            <MuiAvatar
              className={classes.logo}
              variant="square"
              src={show?.logoFile?.url}
            >
              {show?.name?.charAt(0)}
            </MuiAvatar>
            <Typography
              variant="h3"
              component="h2"
              className={classes.showBarText}
            >
              {show?.name}
            </Typography>
          </Grid>
        </Box>
        {exhibitorPair && (
          <Box className={classes.contrastWrapper}>
            <Grid item className={classes.avatarContainer}>
              <Typography
                variant="h3"
                component="h2"
                className={classes.showBarText}
              >
                {exhibitorPair?.companyToShow?.company?.name}
              </Typography>
              <Logo exhibitorPair={exhibitorPair} />
            </Grid>
          </Box>
        )}
      </Grid>
    </MUAppBar>
  );
};

export default ShowBar;
